import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Modal, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { MeasureDataGroup, MeasureDataRound } from "components/Measure-data/Groups-form/GroupsFormContext";
import dayjs from "dayjs";
import { useFacilityForm, useFacilityTabs, useFormatDateHook, useMeasureDrawer, useModals } from "hooks";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import {
  useCreateManyMeasureDataSelect,
  useDealDefaultCurrencySelect,
  useDealIdSelector,
  useDealTitleSelector
} from "store";
import { useDealActions, useDebtActions, useTaskManageActions } from "store/actions-context";
import { AltiusErrors, AltiusRes, DebtSegment, FacilityFee, MeasureDataDraft } from "store/types";
import { defaultFacilityValues, getDisabledFields, getEndOfPeriod } from "utils/debt";

import { AmoritzationDrawerPrincipalSection } from "./AmoritzationDrawerPrincipalSection";
import { AmortizationDrawerInterestSection } from "./AmortizationDrawerInterestSection";
import { AmortizationDrawerPIKSection } from "./AmortizationDrawerPIKSection";
import { FacilityTermsDebtSection } from "./FacilityTermsDebtSection";
import { FacilityTermsDrawerValues } from "./FacilityTermsDrawerValues";
import { FacilityTermsFeesSection } from "./FacilityTermsFeesSection";
import { FacilityTermsPaymentSection } from "./FacilityTermsPaymentSection";

export interface FacilityTermsDrawerProps {
  open: boolean;
  onClose: (refetch?: boolean) => void;
  onApply: (
    segments: DebtSegment[],
    dealId?: number,
    fees?: FacilityFee[]
  ) => Promise<AltiusRes<DebtSegment> | AltiusErrors | void>;
  loading: boolean;
  facilitySegments?: DebtSegment[];
  fees?: FacilityFee[];
  withdrawalSegments?: DebtSegment[];
  openWithdrawalTab?: boolean;
  openNewChangeTab?: { dealId: number; date: string };
}

interface SecondaryTabLabelProps {
  index: number;
  showCloseButton: boolean;
  onRemove: (index: number) => void;
  segments: DebtSegment[];
  currentSecondaryTabIndex: number;
  currentStartDate?: string;
}

const SecondaryTabLabel: React.FC<SecondaryTabLabelProps> = ({
  index,
  showCloseButton,
  onRemove,
  segments,
  currentSecondaryTabIndex,
  currentStartDate
}) => {
  const formatDate = useFormatDateHook();

  const { t } = useTranslation("debt");

  const getSegmentName = (index: number) => {
    if (segments.length === 1) {
      return t("segment.initial");
    }

    if (index === segments.length - 1) {
      return t("segment.current");
    }

    const nextSegmentStartDate =
      index === currentSecondaryTabIndex - 1 ? currentStartDate : segments[index + 1].start_date;
    const startDate = formatDate({ date: segments[index].start_date });
    const endDate = formatDate({ date: dayjs(nextSegmentStartDate).subtract(1, "day") });

    if (index === 0) {
      return t("segment.initialRange", { start: startDate, end: endDate });
    }

    return t("segment.dateRange", { start: startDate, end: endDate });
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <span>{getSegmentName(index)}</span>
      {showCloseButton && (
        <CloseIcon
          sx={{
            fontSize: 16,
            cursor: "pointer",
            "&:hover": { color: "error.main" }
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(index);
          }}
        />
      )}
    </Box>
  );
};

export const FacilityTermsDrawer: React.FC<FacilityTermsDrawerProps> = ({
  open,
  onClose,
  onApply,
  loading,
  facilitySegments = [],
  fees,
  withdrawalSegments = [],
  openWithdrawalTab = false,
  openNewChangeTab = undefined
}) => {
  const { t } = useTranslation("debt");
  const { getDebtIndexValueAction } = useDebtActions();
  const { createManyMeasureDataAction, saveMeasureDataDraftAction, deleteDealAction } = useDealActions();
  const { deleteModal } = useModals();
  const currency = useDealDefaultCurrencySelect();
  const dealId = useDealIdSelector() ?? 0;
  const dealTitle = useDealTitleSelector() ?? "";
  const [isValuesDrawerOpen, setIsValuesDrawerOpen] = useState(false);
  const { rounds } = useMeasureDrawer([dealId]);
  const createMeasureData = useCreateManyMeasureDataSelect();
  const { action: createManyMeasureData, loading: loadingCreateManyMeasureData } =
    useActionHook(createManyMeasureDataAction);
  const { getDealsAction } = useTaskManageActions();

  const { action: deleteDeal } = useActionHook(deleteDealAction, {
    onSuccess: getDealsAction
  });

  const isExistingFacility = Boolean(facilitySegments.length > 0);

  // Use the facility tabs hook for tab management
  const tabs = useFacilityTabs({
    open,
    facilitySegments,
    withdrawalSegments,
    dealId,
    dealTitle,
    isExistingFacility,
    openWithdrawalTab,
    openNewChangeTab,
    defaultValues: defaultFacilityValues
  });

  const {
    primaryTabIndex,
    secondaryTabIndex,
    dealTabs,
    showWarning,
    currentPrimaryTab,
    currentSegments,
    currentSegment,
    isFacilityTab,
    handleAddWithdrawal,
    handlePrimaryTabChange,
    handleSecondaryTabChange,
    handleAddSecondaryTab,
    handleRemoveSecondaryTab,
    handleConfirmTabSwitch,
    handleCancelTabSwitch,
    markTabDirty,
    clearTabDirty,
    updateCurrentSegment,
    resetTabs
  } = tabs;

  // Define handleClose early as it's needed by the form hook
  const handleClose = useCallback(
    (refetch?: boolean) => {
      resetTabs();
      setIsValuesDrawerOpen(false);
      onClose(refetch);
    },
    [resetTabs, onClose]
  );

  // Use the facility form hook
  const { formik, handleSubmit, getSegmentFromFormValues } = useFacilityForm({
    currentSegment,
    currentPrimaryTab,
    isFacilityTab,
    fees,
    facilitySegments,
    primaryTabIndex,
    secondaryTabIndex,
    onApply,
    updateSegmentOnState: updateCurrentSegment,
    clearTabDirty,
    markTabDirty,
    setIsValuesDrawerOpen,
    onClose: () => {
      formik.resetForm();
      handleClose();
    }
  });

  const isWithdrawal = formik.values.is_withdrawal;
  const isFacility = formik.values.is_facility;

  const handleSecondaryTabChangeWithSave = (newValue: number) => {
    const segmentData = getSegmentFromFormValues();
    updateCurrentSegment(segmentData);
    handleSecondaryTabChange(newValue);
  };

  const handleAddSecondaryTabWithValues = () => {
    const segmentData = getSegmentFromFormValues();
    handleAddSecondaryTab(formik.values, segmentData);
  };

  const handleConfirmTabSwitchWithReset = () => {
    const resetForm = () => {
      formik.resetForm();
      formik.dirty = false;
    };
    handleConfirmTabSwitch(resetForm);
  };

  const handleSkip = useCallback(async () => {
    try {
      await handleSubmit();
      handleClose();
    } catch (_error) {
      setIsValuesDrawerOpen(false);
    }
  }, [handleSubmit, handleClose, setIsValuesDrawerOpen]);

  const handleSaveMeasureData = useCallback(
    async (data: { groups: MeasureDataGroup[]; roundsByDeals: Record<number, MeasureDataRound[]> }) => {
      try {
        await handleSubmit();
      } catch (_error) {
        setIsValuesDrawerOpen(false);
        return;
      }
      await createManyMeasureData(data);
      handleClose();
    },
    [createManyMeasureData, handleClose, handleSubmit]
  );

  const handleSaveMeasureDataAsDraft = useCallback(
    async (data: MeasureDataDraft["data"]) => {
      if (!createMeasureData?.dealId) return;

      try {
        await handleSubmit();
      } catch (_error) {
        setIsValuesDrawerOpen(false);
        return;
      }
      await saveMeasureDataDraftAction({
        deal_id: createMeasureData.dealId,
        data,
        ...(createMeasureData.draftId && { id: createMeasureData.draftId })
      });
      handleClose();
    },
    [saveMeasureDataDraftAction, createMeasureData, handleClose, handleSubmit]
  );

  const handleDeleteWithdrawalClick = (tabDealId: number, withdrawalTitle: string, event: React.MouseEvent) => {
    event.stopPropagation();
    deleteModal({
      action: async () => {
        await deleteDeal(tabDealId);
        handleClose(true);
      },
      element: t("facilityDrawer.deleteWithdrawal"),
      message: t("facilityDrawer.deleteWithdrawalMessage", { name: withdrawalTitle })
    });
  };

  const updateIndexValue = async (index: string, date: string | null, type: "principal" | "interest" | "pik") => {
    const referenceDay = date || new Date().toISOString();
    try {
      const value = await getDebtIndexValueAction(index, referenceDay.split("T")[0]);
      formik.setFieldValue(`${type}_index_value`, value);
    } catch (error) {
      console.error("Failed to fetch index value:", error);
    }
  };

  const handleStartDateChange = (newStartDate: dayjs.Dayjs) => {
    // Only update payment dates for withdrawals
    if (!isWithdrawal || !newStartDate) return;

    // Update interest payment date if interest is enabled
    const hasInterest = formik.values.base_rate || formik.values.interest_reference_index;
    if (hasInterest && formik.values.interest_frequency && !formik.values.interest_first_payment_date) {
      const interestPaymentDate = getEndOfPeriod(newStartDate, formik.values.interest_frequency);
      formik.setFieldValue("interest_first_payment_date", interestPaymentDate);
    }

    // Update PIK dates if PIK is enabled
    const hasPik = formik.values.pik_base_rate || formik.values.pik_reference_index;
    if (hasPik) {
      if (formik.values.pik_capitalization_frequency && !formik.values.pik_first_capitalization_date) {
        const pikCapitalizationDate = getEndOfPeriod(newStartDate, formik.values.pik_capitalization_frequency);
        formik.setFieldValue("pik_first_capitalization_date", pikCapitalizationDate);
      }
    }

    // Update loan status date if loan status frequency is enabled
    if (formik.values.loan_status_frequency && !formik.values.loan_first_status_date) {
      const loanStatusDate = getEndOfPeriod(newStartDate, formik.values.loan_status_frequency);
      formik.setFieldValue("loan_first_status_date", loanStatusDate);
    }

    // Update principal payment date if principal is enabled
    if (
      ["fixed_principal", "fixed_total"].includes(formik.values.payment_term) &&
      !formik.values.principal_first_payment_date
    ) {
      const principalPaymentDate = getEndOfPeriod(newStartDate, formik.values.principal_frequency);
      formik.setFieldValue("principal_first_payment_date", principalPaymentDate);
    }
  };

  // Principal section is always shown, but first payment date is auto-filled to maturity for balloon modes
  const showPrincipalSection = !["custom"].includes(formik.values.payment_term);

  // Calculate which fields should be disabled based on configuration
  const disabledFields = getDisabledFields({
    paymentTerm: formik.values.payment_term,
    isWithdrawal,
    overridingParent: formik.values.overriding_parent,
    isFacility: formik.values.is_facility
  });

  return (
    <>
      <Modal
        open={open}
        onClose={() => handleClose()}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Paper
          sx={{
            width: "90vw",
            maxWidth: "1200px",
            height: "90vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: "20px"
          }}
        >
          <AsyncContentMui spinnerProps={{ size: 100 }} isLoading={loading}>
            <form onSubmit={formik.handleSubmit} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
              {/* Header */}
              <Box
                sx={{
                  p: 5,
                  borderBottom: 1,
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <Typography variant="h5">
                  {isExistingFacility ? t("facilityDrawer.editTitle") : t("facilityDrawer.createTitle")}
                </Typography>
                <IconButton onClick={() => handleClose()}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Primary Tabs (Facility + Withdrawals) */}
              {isExistingFacility && (
                <>
                  <Box sx={{ borderBottom: 1, borderColor: "divider", pr: 5, display: "flex", alignItems: "center" }}>
                    <Tabs
                      value={primaryTabIndex}
                      onChange={handlePrimaryTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      {dealTabs.map((tab) => {
                        const hasDeleteButton = !tab.isFacility && !tab.isNew;
                        return (
                          <Tab
                            key={tab.dealId}
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  position: "relative",
                                  pr: tab.isDirty ? 2 : !tab.isFacility ? 2.5 : 0
                                }}
                              >
                                {tab.dealTitle}
                                {tab.isDirty && (
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      bgcolor: "primary.main",
                                      position: "absolute",
                                      right: hasDeleteButton ? 23 : -5,
                                      top: "50%",
                                      transform: "translateY(-50%)"
                                    }}
                                  />
                                )}
                                {hasDeleteButton && (
                                  <CloseIcon
                                    sx={{
                                      fontSize: 16,
                                      ml: tab.isDirty ? 2 : 1,
                                      cursor: "pointer",
                                      "&:hover": { color: "error.main" }
                                    }}
                                    onClick={(e) => handleDeleteWithdrawalClick(tab.dealId, tab.dealTitle, e)}
                                  />
                                )}
                              </Box>
                            }
                          />
                        );
                      })}
                    </Tabs>
                    {formik.values.is_facility && !formik.values.is_withdrawal && (
                      <Button
                        variant="text"
                        startIcon={<AddIcon />}
                        onClick={handleAddWithdrawal}
                        size="sm"
                        sx={{ width: "24px", minWidth: "24px" }}
                      />
                    )}
                  </Box>

                  {/* Secondary Tabs (Segments within a deal) */}
                  {currentSegments.length > 0 && (
                    <Box sx={{ px: 5 }}>
                      <Box sx={{ borderBottom: 1, borderColor: "#DBE8F5", display: "flex", alignItems: "center" }}>
                        <Tabs
                          value={secondaryTabIndex}
                          onChange={(_, newValue) => handleSecondaryTabChangeWithSave(newValue)}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{
                            mb: "-1px",
                            "& .MuiTabs-indicator": {
                              zIndex: 1
                            }
                          }}
                        >
                          {currentSegments.map((segment, index) => (
                            <Tab
                              key={`${segment.id}-${index}`}
                              label={
                                <SecondaryTabLabel
                                  index={index}
                                  showCloseButton={
                                    currentSegments.length > 1 && (isFacility || formik.values.overriding_parent)
                                  }
                                  onRemove={handleRemoveSecondaryTab}
                                  segments={currentSegments}
                                  currentSecondaryTabIndex={secondaryTabIndex}
                                  currentStartDate={formik.values.start_date}
                                />
                              }
                            />
                          ))}
                        </Tabs>
                        {(formik.values.is_facility || formik.values.overriding_parent) && (
                          <IconButton size="sm" onClick={handleAddSecondaryTabWithValues} sx={{ ml: 1 }}>
                            <AddIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  )}
                </>
              )}

              <Stack
                spacing={5}
                sx={{
                  p: 5,
                  flex: 1,
                  overflow: "auto"
                }}
              >
                {/* Payment Terms Section */}
                <FacilityTermsPaymentSection formik={formik} hasWithdrawals={dealTabs.some((tab) => !tab.isFacility)} />

                {/* Debt Section */}
                <FacilityTermsDebtSection
                  formik={formik}
                  currency={currency || "USD"}
                  isFirstSegment={secondaryTabIndex === 0}
                  disabledFields={disabledFields}
                  onStartDateChange={handleStartDateChange}
                />

                {showPrincipalSection && (
                  <AmoritzationDrawerPrincipalSection
                    formik={formik}
                    updateIndexValue={updateIndexValue}
                    size="large"
                    disabledFields={disabledFields}
                    hiddenFields={["balloon_payment"]}
                  />
                )}

                <AmortizationDrawerInterestSection
                  formik={formik}
                  updateIndexValue={updateIndexValue}
                  size="large"
                  disabledFields={disabledFields}
                />

                <AmortizationDrawerPIKSection
                  formik={formik}
                  updateIndexValue={updateIndexValue}
                  size="large"
                  disabledFields={disabledFields}
                />

                {!isWithdrawal && <FacilityTermsFeesSection formik={formik} />}
              </Stack>

              {/* Footer */}
              <Paper
                elevation={8}
                sx={{
                  p: 5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0px -4px 8px rgba(0, 0, 0, 0.1)",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 5,
                  bgcolor: "background.paper",
                  zIndex: 1
                }}
              >
                <Button variant="outlined" onClick={() => handleClose()}>
                  {t("common.cancel")}
                </Button>
                <Button variant="contained" type="submit">
                  {currentPrimaryTab?.isNew ? t("common.next") : t("common.update")}
                </Button>
              </Paper>
            </form>
          </AsyncContentMui>
        </Paper>
      </Modal>

      {rounds && (
        <FacilityTermsDrawerValues
          open={isValuesDrawerOpen}
          rounds={rounds}
          onClose={() => setIsValuesDrawerOpen(false)}
          segments={[formik.values]}
          loading={loadingCreateManyMeasureData}
          onSave={handleSaveMeasureData}
          onSaveAsDraft={handleSaveMeasureDataAsDraft}
          onSkip={handleSkip}
        />
      )}

      {/* Warning Modal for Unsaved Changes */}
      <Modal
        open={showWarning}
        onClose={handleCancelTabSwitch}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Paper
          sx={{
            width: "400px",
            p: 5,
            display: "flex",
            flexDirection: "column",
            gap: 3
          }}
        >
          <Typography variant="h6">{t("facilityDrawer.unsavedChangesTitle")}</Typography>
          <Typography>{t("facilityDrawer.unsavedChangesMessage")}</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={handleCancelTabSwitch}>
              {t("common.cancel")}
            </Button>
            <Button variant="contained" color="primary" onClick={handleConfirmTabSwitchWithReset}>
              {t("facilityDrawer.discardChanges")}
            </Button>
          </Box>
        </Paper>
      </Modal>
    </>
  );
};
